<template>
  <div>
    <!-- ================================================================
         Dialog hiển thị chi tiết và danh sách phiếu thu của học sinh
         Sử dụng: <student-invoice-detail-dialog ref="detailDialog" @saved="$emit('saved')" />
         Mở:      this.$refs.detailDialog.open(studentRow)
         ================================================================ -->
    <dialog-base
      :visible.sync="visible"
      :title="dialogTitle"
      size="xl"
      :show-confirm="false"
      cancel-text="Đóng"
      cancel-type="danger"
      :use-panel="false"
      top="4vh"
      @cancel="handleClose"
    >
      <!-- Thông tin học sinh Header -->
      <div class="sk-panel mb-4" style="margin-bottom: 10px">
        <div class="sk-panel-head sidd-panel-head" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
          <div class="sidd-student-info" style="display: flex; align-items: center;">
            <div class="sk-panel-icon sk-panel-icon--primary" style="margin-right: 15px;">
              <i class="el-icon-user" />
            </div>
            <div>
              <p class="sk-panel-title">{{ studentData.studentName || 'Đang tải...' }}</p>
              <p class="sk-panel-subtitle">
                Mã học sinh: <strong>{{ studentData.studentCode || '—' }}</strong> &mdash; 
                Lớp: <strong>{{ studentData.className || '—' }}</strong> &mdash; 
                Tổng số phiếu thu: <strong>{{ (studentData.invoices || []).length }}</strong>
              </p>
            </div>
          </div>
          <div class="sidd-panel-actions" style="display: flex; justify-content: flex-end;">
            <el-button
              v-if="checkPermission(['tuition_excel_export_v4'])"
              type="success"
              size="small"
              icon="el-icon-download"
              :loading="isExporting"
              :disabled="!studentData.invoices || studentData.invoices.length === 0"
              @click="handleExportExcel"
            >
              Xuất Excel
            </el-button>
          </div>
        </div>
      </div>

      <!-- Thẻ thống kê KPI -->
      <div class="sidd-kpi-grid mb-4">
        <div class="sidd-kpi-card" v-for="card in kpiCards" :key="card.label">
          <div class="sidd-kpi-icon" :class="card.iconClass">
            <i :class="card.icon" />
          </div>
          <div class="sidd-kpi-body">
            <span class="sidd-kpi-label">{{ card.label }}</span>
            <strong class="sidd-kpi-value" :class="card.valueClass">{{ card.value }}</strong>
          </div>
        </div>
      </div>

      <!-- Bảng danh sách phiếu thu -->
      <div class="sidd-table-section">
        <div class="sidd-section-head mb-2">
          <p class="sidd-section-title">
            <i class="el-icon-document" /> Danh sách phiếu thu chi tiết
          </p>
        </div>

        <div class="sidd-table-wrapper">
          <el-table
            :data="studentData.invoices || []"
            class="sk-table sidd-main-table"
            border
            stripe
            :empty-text="'Học sinh này chưa có phiếu thu nào.'"
            max-height="420"
          >
            <!-- STT -->
            <el-table-column
              type="index"
              label="STT"
              width="50"
              align="center"
            />

            <!-- Tháng -->
            <el-table-column
              label="Tháng"
              width="95"
              align="center"
            >
              <template slot-scope="{ row }">
                <span>{{ formatMonth(row) }}</span>
              </template>
            </el-table-column>

            <!-- Đợt thu -->
            <el-table-column
              prop="turn"
              label="Đợt"
              width="65"
              align="center"
            />

            <!-- Phạm vi -->
            <el-table-column label="Phạm vi" width="105" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="typeTagType(row.targetType)" size="mini" effect="light">
                  {{ typeLabel(row.targetType) }}
                </el-tag>
              </template>
            </el-table-column>

            <!-- Đối tượng (Lớp / Nhóm / Gói) -->
            <el-table-column
              prop="targetName"
              label="Lớp / Nhóm / Gói"
              min-width="170"
            />

            <!-- Khoảng áp dụng -->
            <el-table-column
              label="Khoảng áp dụng"
              min-width="180"
              align="center"
            >
              <template slot-scope="{ row }">
                <span>{{ formatDate(row.startValidityPeriod) }} &mdash; {{ formatDate(row.endValidityPeriod) }}</span>
              </template>
            </el-table-column>

            <!-- Trạng thái phiếu thu -->
            <el-table-column
              label="Trạng thái"
              width="135"
              align="center"
            >
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="mini" effect="light">
                  {{ statusLabel(row.status) }}
                </el-tag>
              </template>
            </el-table-column>

            <!-- Phải thu -->
            <el-table-column
              label="Phải thu"
              align="right"
              min-width="130"
              class-name="sidd-finance-column"
              label-class-name="sidd-finance-header"
            >
              <template slot-scope="{ row }">
                <span class="sidd-money sidd-money--bold">{{ row.sumMoney | formatCurrency }}</span>
              </template>
            </el-table-column>

            <!-- Đã thu -->
            <el-table-column
              label="Đã thu"
              align="right"
              min-width="130"
              class-name="sidd-finance-column"
              label-class-name="sidd-finance-header"
            >
              <template slot-scope="{ row }">
                <span class="sidd-money">{{ row.collectedMoney | formatCurrency }}</span>
              </template>
            </el-table-column>

            <!-- Còn lại -->
            <el-table-column
              label="Thiếu / Thừa"
              align="right"
              min-width="130"
              class-name="sidd-finance-column"
              label-class-name="sidd-finance-header"
            >
              <template slot-scope="{ row }">
                <span :class="row.afterMoney > 0 ? 'sidd-money--positive' : 'sidd-money--negative'">
                  {{ row.afterMoney | formatCurrency }}
                </span>
              </template>
            </el-table-column>

            <!-- Tác vụ -->
            <el-table-column label="Tác vụ" width="145" align="center" fixed="right">
              <template slot-scope="{ row }">
                <div class="sidd-action-buttons">
                  <el-tooltip content="Chi tiết / Thanh toán" placement="top" :enterable="false">
                    <el-button
                      v-if="checkPermission(['tuition_payment_v4'])"
                      size="mini"
                      type="primary"
                      icon="el-icon-wallet"
                      circle
                      @click="handleOpenInvoice(row)"
                    />
                  </el-tooltip>
                  <el-tooltip content="Lịch sử thanh toán" placement="top" :enterable="false">
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-tickets"
                      circle
                      @click="handlePaymentHistory(row)"
                    />
                  </el-tooltip>
                  <el-tooltip content="Lịch sử thao tác" placement="top" :enterable="false">
                    <el-button
                      size="mini"
                      type="warning"
                      icon="el-icon-time"
                      circle
                      @click="handleActionHistory(row)"
                    />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </dialog-base>

    <!-- Sub-dialog: Thanh toán / Chi tiết phiếu thu -->
    <invoice-payment-dialog
      ref="invoicePaymentDialog"
      @saved="handleInvoiceSaved"
    />

    <!-- Sub-dialog: Lịch sử thanh toán -->
    <invoice-payment-history-dialog
      ref="invoicePaymentHistoryDialog"
    />

    <!-- Sub-dialog: Lịch sử thao tác (Audit trail) -->
    <invoice-action-history-dialog
      ref="invoiceActionHistoryDialog"
    />
  </div>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import checkPermission from "@/utils/permission";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { exportStudentInvoiceDetailExcel } from "@/commonFuncion/exportStudentInvoiceDetailExcel";
import InvoicePaymentDialog from "@/components/tutition_v3/dialog/InvoicePaymentDialog.vue";
import InvoicePaymentHistoryDialog from "@/components/tutition_v3/dialog/InvoicePaymentHistoryDialog.vue";
import InvoiceActionHistoryDialog from "@/components/tutition_v3/dialog/InvoiceActionHistoryDialog.vue";

export default {
  name: "StudentInvoiceDetailDialog",
  components: {
    DialogBase,
    InvoicePaymentDialog,
    InvoicePaymentHistoryDialog,
    InvoiceActionHistoryDialog,
  },

  data() {
    return {
      visible: false,
      isExporting: false,
      studentData: {
        studentName: "",
        studentCode: "",
        className: "",
        summary: {},
        invoices: [],
      },
    };
  },

  computed: {
    dialogTitle() {
      if (!this.studentData.studentName) return "Chi tiết phiếu thu học sinh";
      return `Chi tiết phiếu thu: ${this.studentData.studentName} (${this.studentData.studentCode || '—'})`;
    },

    kpiCards() {
      const s = this.studentData.summary || {};
      return [
        {
          label: "Tổng phiếu thu",
          value: s.totalInvoices || 0,
          icon: "el-icon-document-copy",
          iconClass: "sidd-kpi-icon--primary",
        },
        {
          label: "Phiếu theo lớp",
          value: s.classInvoiceCount || 0,
          icon: "el-icon-school",
          iconClass: "sidd-kpi-icon--class",
        },
        {
          label: "Phiếu theo nhóm",
          value: s.groupInvoiceCount || 0,
          icon: "el-icon-user",
          iconClass: "sidd-kpi-icon--group",
        },
        {
          label: "Phiếu theo gói",
          value: s.packageInvoiceCount || 0,
          icon: "el-icon-box",
          iconClass: "sidd-kpi-icon--package",
        },
        {
          label: "Tổng tiền phải thu",
          value: this.formatCurrencyText(s.totalMoney || 0),
          icon: "el-icon-money",
          iconClass: "sidd-kpi-icon--warning",
        },
        {
          label: s.remainingMoney > 0 ? "Thiếu còn lại" : "Thừa còn lại",
          value: this.formatCurrencyText(Math.abs(s.remainingMoney || 0)),
          icon: "el-icon-wallet",
          iconClass: s.remainingMoney > 0 ? "sidd-kpi-icon--danger" : "sidd-kpi-icon--success",
          valueClass: s.remainingMoney > 0 ? "sidd-text-danger" : "sidd-text-success",
        },
      ];
    },
  },

  methods: {
    checkPermission,

    /**
     * Mở Dialog hiển thị chi tiết của học sinh
     * @param {Object} studentRow
     */
    open(studentRow) {
      this.studentData = {
        ...studentRow,
        className: studentRow.className || "",
        summary: studentRow.summary || {},
        invoices: Array.isArray(studentRow.invoices) ? studentRow.invoices : [],
      };
      this.visible = true;
    },

    handleClose() {
      this.visible = false;
    },

    /**
     * Mở Dialog thanh toán / chi tiết phiếu thu
     */
    handleOpenInvoice(invoice) {
      if (!this.$refs.invoicePaymentDialog) return;
      const payload = {
        ...invoice,
        fullName: this.studentData.studentName,
        studentName: this.studentData.studentName,
        studentCode: this.studentData.studentCode,
      };
      this.$refs.invoicePaymentDialog.open(payload);
    },

    /**
     * Mở Dialog lịch sử thanh toán phiếu thu
     */
    handlePaymentHistory(invoice) {
      if (!this.$refs.invoicePaymentHistoryDialog) return;
      const payload = {
        ...invoice,
        fullName: this.studentData.studentName,
        studentName: this.studentData.studentName,
        studentCode: this.studentData.studentCode,
      };
      this.$refs.invoicePaymentHistoryDialog.open(payload);
    },

    /**
     * Mở Dialog lịch sử thao tác phiếu thu
     */
    handleActionHistory(invoice) {
      if (!this.$refs.invoiceActionHistoryDialog) return;
      const payload = {
        ...invoice,
        fullName: this.studentData.studentName,
        studentName: this.studentData.studentName,
        studentCode: this.studentData.studentCode,
      };
      this.$refs.invoiceActionHistoryDialog.open(payload);
    },

    handleInvoiceSaved() {
      this.$emit("saved");
      this.handleClose();
    },

    /**
     * Xuất danh sách phiếu thu chi tiết của học sinh ra Excel
     */
    async handleExportExcel() {
      if (!this.studentData.invoices || this.studentData.invoices.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Học sinh chưa có phiếu thu để xuất Excel.");
        return;
      }
      this.isExporting = true;
      try {
        await exportStudentInvoiceDetailExcel({
          invoices: this.studentData.invoices,
          studentName: this.studentData.studentName,
          studentCode: this.studentData.studentCode,
          className: this.studentData.className,
          summary: this.studentData.summary,
        });
        NotifyMessage.leftCornerNotificationSuccess("Xuất Excel phiếu thu học sinh thành công.");
      } catch (error) {
        console.error(error);
        NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra khi xuất file Excel.");
      } finally {
        this.isExporting = false;
      }
    },

    typeLabel(type) {
      const map = {
        CLASS: "Lớp",
        GROUP: "Nhóm",
        PACKAGE: "Gói",
      };
      return map[type] || type || "—";
    },

    typeTagType(type) {
      const map = {
        CLASS: "primary",
        GROUP: "warning",
        PACKAGE: "success",
      };
      return map[type] || "info";
    },

    statusTagType(status) {
      switch (status) {
        case "FULLY_PAID":
        case "FULLY_PAID_WITH_SURPLUS":
          return "success";
        case "PARTIALLY_PAID":
        case "PAID_WITH_DEFICIT":
          return "warning";
        case "UNPAID":
          return "danger";
        case "HIDE":
          return "info";
        case "OVERPAID":
          return "success";
        default:
          return "info";
      }
    },

    statusLabel(status) {
      const map = {
        UNBILLED: "Chưa có phiếu thu",
        UNPAID: "Chưa đóng 100%",
        PARTIALLY_PAID: "Chưa đóng đủ",
        FULLY_PAID: "Đóng đủ 100%",
        OVERPAID: "Đóng thừa",
        FULLY_PAID_WITH_SURPLUS: "Đóng đủ thừa",
        PAID_WITH_DEFICIT: "Đóng đủ thiếu",
        HIDE: "Ẩn phiếu thu",
      };
      return map[status] || status || "—";
    },

    formatMonth(row) {
      if (row.month && row.year) {
        return `${row.month}/${row.year}`;
      }
      if (row.startValidityPeriod) {
        const parts = String(row.startValidityPeriod).split("-");
        if (parts.length >= 2) {
          return `${parseInt(parts[1], 10)}/${parts[0]}`;
        }
      }
      return "—";
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const parts = String(dateStr).split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateStr;
    },

    formatCurrencyText(val) {
      return Number(val || 0).toLocaleString("vi-VN") + " ₫";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

.sidd-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 0 !important;
}

.sidd-student-info {
  display: flex;
  align-items: center;
}

.sidd-panel-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 0 0 auto !important;
}

/* ─── KPI Cards Grid ─────────────────────────────────── */
.sidd-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
}

.sidd-kpi-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
    border-color: #cbd5e1;
    background: #ffffff;
  }
}

.sidd-kpi-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;

  &--primary {
    background: #eff6ff;
    color: #2563eb;
  }
  &--class {
    background: #e0f2fe;
    color: #0284c7;
  }
  &--group {
    background: #fef3c7;
    color: #d97706;
  }
  &--package {
    background: #dcfce7;
    color: #16a34a;
  }
  &--money {
    background: #eff6ff;
    color: #1d4ed8;
  }
  &--danger {
    background: #fee2e2;
    color: #dc2626;
  }
  &--success {
    background: #dcfce7;
    color: #16a34a;
  }
}

.sidd-kpi-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sidd-kpi-label {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 2px;
}

.sidd-kpi-value {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidd-text-primary {
  color: #2563eb !important;
}

.sidd-text-danger {
  color: #dc2626 !important;
}

.sidd-text-success {
  color: #16a34a !important;
}

/* ─── Section Title ──────────────────────────────────── */
.sidd-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidd-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;

  i {
    color: #2563eb;
    margin-right: 4px;
  }
}

/* ─── Table Wrapper & Header ─────────────────────────── */
.sidd-table-wrapper {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
}

/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

/deep/ .sk-table thead tr {
  height: 48px;
}

/* ─── Finance Column ─────────────────────────────────── */
/deep/ .sk-table thead th.sidd-finance-header,
/deep/ .sk-table thead td.sidd-finance-header {
  background: #0284c7 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}

/deep/ .sk-table tbody td.sidd-finance-column {
  background-color: #f0fdfa !important;
}

.sidd-money {
  font-weight: 600;
  font-size: 13px;

  &--bold {
    font-weight: 700;
    color: #0f172a;
  }

  &--positive {
    color: #dc2626;
    font-weight: 700;
  }

  &--negative {
    color: #16a34a;
    font-weight: 700;
  }
}

.sidd-action-buttons {
  display: flex;
  justify-content: center;
  gap: 6px;
}

/deep/ .el-button.is-circle {
  border-radius: 50% !important;
}
</style>
